// FileIngestionUploadSection.tsx
import React from 'react';
import { Upload, Button, List, Spin, Space, Alert, Progress, Card } from 'antd';
import ExchangeRate from '@components/exchange-rate/ExchangeRate';
import { UploadOutlined, DeleteOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import useFileIngestionUploadSection from './useFileIngestionUploadSection';
import styles from './FileIngestionUploadSection.module.css';
import { NSPButton } from '@components/ui/NSPButton';

interface ValidationMessageProps {
  heading: string;
  message?: string;
}

const ValidationMessage = ({ heading, message }: ValidationMessageProps) => {
  const isMultiLine = message?.includes('\n');

  return (
    <div>
      <b style={{ fontSize: '16px' }}>{heading}</b>

      {message && !isMultiLine && (
        <div style={{ marginTop: '6px' }}>
          {message}
        </div>
      )}

      {message && isMultiLine && (
        <details style={{ marginTop: '8px', cursor: 'pointer' }}>
          <summary style={{ fontSize: '15px' }}>
            View Details
          </summary>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }}>
            {message}
          </pre>
        </details>
      )}
    </div>
  );
};

const getProgressStatus = (status: string): 'active' | 'success' | 'exception' | 'normal' => {
  if (status === 'scanning' || status === 'starting' || status === 'warming_up' || status === 'connected') {
    return 'active';
  }
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'failed' || status === 'cancelled') {
    return 'exception';
  }
  return 'normal';
};

const FileIngestionUploadSection = () => {
  const {
    fileList,
    uploading,
    uploadingAll,
    uploadLocked,
    validationFeedback,
    uploadProgress,
    isProcessing,
    handleSelectFile,
    handleRemoveFile,
    handleUploadAll,
    handleCloseProgress,
  } = useFileIngestionUploadSection();

  const showProgress = uploadProgress.status !== 'idle';
  const canClose = ['completed', 'failed', 'cancelled', 'error'].includes(uploadProgress.status);


  return (
    <div className={styles.uploadCard} style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className={styles.headerTitle}>Upload New File</div>

        {validationFeedback && (
          <Alert
            type={validationFeedback.type}
            showIcon
            closable
            message={
              <ValidationMessage
                heading={validationFeedback.heading}
                message={validationFeedback.message}
              />
            }
          />
        )}


        {/* Info alert when processing */}
        {isProcessing && (
          <Alert
            message="Processing in progress"
            description="Please wait for the current files to finish processing before uploading new files. This page will update automatically."
            type="info"
            showIcon
          />
        )}

        {/* Lock alert when another user's pipeline or dataflow is running.
            Suppressed while this user's own upload request is in flight, so
            they aren't told uploads are "disabled" by the very action they're
            in the middle of taking. */}
        {uploadLocked && !isProcessing && !uploading && !uploadingAll && (
          <Alert
            message="Upload temporarily disabled"
            description="A pipeline is currently running. File upload will be re-enabled automatically once processing is complete."
            type="warning"
            showIcon
          />
        )}

        {/* Fixed Progress Bar - stays visible until manually closed */}
        {showProgress && (
          <Card
            size="small"
            className={styles.progressbar}
            extra={
              canClose && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCloseProgress}
                />
              )
            }
          >
            <Progress
              percent={uploadProgress.progress}
              status={getProgressStatus(uploadProgress.status)}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div className={styles.uploadProgress}>
              {uploadProgress.message}
            </div>
          </Card>
        )}

        <div className={styles.refreshbtn}>
          <div className={styles.leftActions}>
            <Upload
              beforeUpload={handleSelectFile}
              multiple
              showUploadList={false}
              disabled={uploading || uploadingAll || isProcessing || uploadLocked}
            >
              <NSPButton
                icon={<UploadOutlined />}
                data-testid="select-files-btn"
                disabled={uploading || uploadingAll || isProcessing || uploadLocked}
              >
                Select Files
              </NSPButton>
            </Upload>

            <NSPButton
              onClick={handleUploadAll}
              disabled={!fileList.length || uploadingAll || isProcessing || uploadLocked}
              data-testid="upload-all-btn"
            >
              {uploadingAll ? <Spin size="small" /> : 'Upload All'}
            </NSPButton>

            {fileList.length > 0 && (
              <List
                bordered
                size="small"
                dataSource={fileList}
                renderItem={(file) => (
                  <List.Item
                    key={file.uid}
                    actions={[
                      <Button
                        key="delete"
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(file)}
                        disabled={isProcessing}
                      />,
                    ]}
                  >
                    {file.name}
                  </List.Item>
                )}
              />
            )}
          </div>

          <div className={styles.exchangeRateSlot}>
            <ExchangeRate />
          </div>

          <NSPButton
            icon={<ReloadOutlined />}
            onClick={() => globalThis.location.reload()}
          >
            Refresh
          </NSPButton>
        </div>
      </Space>
    </div>
  );
};

export default FileIngestionUploadSection;